/* eslint react/jsx-handler-names: ['off'] -- controllerProps handler names are ok */

import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { TextField } from '@mui/material'
import { TextFieldProps } from '@mui/material/TextField'
import { Autocomplete } from '@mui/lab' // createFilterOptions, AutocompleteProps
import { createFilterOptions, AutocompleteProps } from '@mui/material/Autocomplete'

import withFormFunctionality, { FormElementProps } from '~shared/components/reactHookForm/withFormFunctionality.hoc'
import { getOptionByValue } from '~utils/getOptionByValue'
import { AutocompleteOption, PossibleAutoCompleteValue } from '~shared/models/AutocompleteOption'

export type Props = Omit<AutocompleteProps<
  AutocompleteOption, // T    -> option type
  false, // Multiple          -> allow multiple selections
  false | true, // DisableClearable  -> value cannot be emptied after selection
  false // FreeSolo           -> input does not have to be a option
  >, 'renderInput' | 'onChange' | 'onBlur' | 'value' | 'defaultValue'> & FormElementProps & {
  options: AutocompleteOption[]
  onChange?: (option: AutocompleteOption | null) => PossibleAutoCompleteValue | void
  defaultValue?: PossibleAutoCompleteValue // is there a way to have this type be a value of a option provided by the sibling "options" ?
  textFieldProps?: TextFieldProps
}

const FormAutocomplete: React.FC<Props> = (props) => {
  const {
    label,
    validationError,
    defaultValue,
    options,
    helperText,
    required,
    textFieldProps,
    ...rest
  } = props

  const { control } = useFormContext()
  const filterOptions = createFilterOptions<AutocompleteOption>({ limit: 100 })

  return <Controller
    control={control}
    name={props.name}
    defaultValue={defaultValue || null}
    render={(controllerProps) => {
      return <Autocomplete
        openOnFocus
        autoHighlight
        options={options}
        value={getOptionByValue(controllerProps.value, options)}
        getOptionLabel={option => option.label}
        isOptionEqualToValue={(option, selectedOption) => option.value === selectedOption.value}
        renderOption={(renderOptionProps, option) => (
          <li {...renderOptionProps}>
            {option.label}
          </li>
        )}
        filterOptions={filterOptions}
        {...rest}
        renderInput={autocompleteRenderInputParams => {
          return <TextField
            {...autocompleteRenderInputParams}
            label={label}
            variant='standard'
            required={required}
            error={!!validationError}
            helperText={validationError?.message || helperText}
            {...textFieldProps}
            inputProps={{
              ...autocompleteRenderInputParams?.inputProps,
              ...textFieldProps?.inputProps,
              // see: https://material-ui.com/components/autocomplete/#autocomplete-autofill
              autoComplete: 'new-password', // disable browser autofill
            }}
          />
        }}
        onChange={(changeEvent, selectedOption) => {
          let onChangeValue = selectedOption ? selectedOption.value : undefined
          if (props.onChange) {
            const newValue = props.onChange(selectedOption)
            !!newValue && (onChangeValue = newValue)
          }

          controllerProps.onChange(onChangeValue || null)
        }}
        onBlur={controllerProps.onBlur}
      />
    }}
  />
}

export default withFormFunctionality<Props>(FormAutocomplete)
